/**
 * useSpeechSynthesis Hook
 *
 * A React hook for text-to-speech functionality using the Web Speech API.
 * Provides voice output capabilities with customizable voice, rate, and pitch.
 *
 * Features:
 * - Text-to-speech with multiple voices
 * - Adjustable speech rate and pitch
 * - Pause, resume, and cancel controls
 * - Queue management for multiple utterances
 * - Works offline (uses local synthesis)
 *
 * @example
 * const {
 *   speak,
 *   cancel,
 *   pause,
 *   resume,
 *   isSpeaking,
 *   isPaused,
 *   voices,
 *   currentVoice,
 *   setVoice,
 *   rate,
 *   setRate,
 *   pitch,
 *   setPitch,
 *   browserSupported
 * } = useSpeechSynthesis();
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Check for browser support
const browserSupported = typeof window !== 'undefined' && 'speechSynthesis' in window;

// Preferred female voice patterns for natural, soft speech
const PREFERRED_FEMALE_VOICES = [
  'Samantha', 'Karen', 'Moira', 'Tessa', 'Fiona', 'Victoria',
  'Google US English', 'Google UK English Female',
  'Microsoft Zira', 'Microsoft Hazel', 'Microsoft Susan',
  'Aria', 'Jenny', 'Sara',
];

// Find preferred female voice from available voices
const findPreferredFemaleVoice = (voices, langPrefix = 'en') => {
  const languageVoices = voices.filter((v) => v.lang.startsWith(langPrefix));
  const candidateVoices = languageVoices.length > 0 ? languageVoices : voices.filter((v) => v.lang.startsWith('en'));

  // Try preferred voice patterns first
  for (const pattern of PREFERRED_FEMALE_VOICES) {
    const match = candidateVoices.find((v) => v.name.includes(pattern));
    if (match) return match;
  }

  // Look for any likely female voice
  const femaleVoice = candidateVoices.find((v) => {
    const name = v.name.toLowerCase();
    return name.includes('female') || name.includes('woman') ||
      /\b(samantha|karen|victoria|zira|hazel|susan|aria|jenny|sara|allison|ava|joanna|kendra|kimberly|salli|ivy|nicole|emma|amy|raveena|aditi|lupe|penelope|camila|lucia|mia|conchita|zhiyu|mizuki|nanami|seoyeon|sunhi)\b/i.test(name);
  });

  return femaleVoice || candidateVoices[0] || voices[0];
};

/**
 * Custom hook for text-to-speech functionality
 * @param {Object} options - Configuration options
 * @param {string} options.language - Preferred language code (default: 'en-US')
 * @param {number} options.defaultRate - Default speech rate 0.5-2 (default: 1)
 * @param {number} options.defaultPitch - Default pitch 0-2 (default: 1)
 * @param {function} options.onStart - Callback when speech starts
 * @param {function} options.onEnd - Callback when speech ends
 * @param {function} options.onError - Callback when error occurs
 * @returns {Object} Speech synthesis state and controls
 */
const useSpeechSynthesis = (options = {}) => {
  const {
    language = 'en-US',
    defaultRate = 0.92, // Slightly slower for natural, conversational feel
    defaultPitch = 1.08, // Slightly higher for softer female voice tone
    onStart,
    onEnd,
    onError,
  } = options;

  const [voices, setVoices] = useState([]);
  const [currentVoice, setCurrentVoice] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rate, setRate] = useState(defaultRate);
  const [pitch, setPitch] = useState(defaultPitch);
  const [error, setError] = useState(null);

  const utteranceRef = useRef(null);

  // Load available voices
  useEffect(() => {
    if (!browserSupported) {
      return;
    }

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();

      if (availableVoices.length > 0) {
        setVoices(availableVoices);

        // Find preferred female voice for natural, soft speech
        const langPrefix = language.split('-')[0];
        const selectedVoice = findPreferredFemaleVoice(availableVoices, langPrefix);
        setCurrentVoice(selectedVoice);
      }
    };

    // Load voices immediately if available
    loadVoices();

    // Chrome loads voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [language]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (browserSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Speak text
  const speak = useCallback(
    (text, speakOptions = {}) => {
      if (!browserSupported) {
        setError('Speech synthesis is not supported in this browser.');
        return;
      }

      if (!text || text.trim() === '') {
        return;
      }

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      // Apply settings
      utterance.voice = speakOptions.voice || currentVoice;
      utterance.rate = speakOptions.rate ?? rate;
      utterance.pitch = speakOptions.pitch ?? pitch;
      utterance.volume = speakOptions.volume ?? 1;
      utterance.lang = speakOptions.language || language;

      // Event handlers
      utterance.onstart = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setError(null);

        if (onStart) {
          onStart();
        }
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setIsPaused(false);

        if (onEnd) {
          onEnd();
        }
      };

      utterance.onerror = (event) => {
        // Ignore 'interrupted' errors (happens when cancel is called)
        if (event.error === 'interrupted') {
          return;
        }

        const errorMessage = `Speech synthesis error: ${event.error}`;
        setError(errorMessage);
        setIsSpeaking(false);
        setIsPaused(false);

        if (onError) {
          onError(event.error, errorMessage);
        }
      };

      utterance.onpause = () => {
        setIsPaused(true);
      };

      utterance.onresume = () => {
        setIsPaused(false);
      };

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [currentVoice, rate, pitch, language, onStart, onEnd, onError]
  );

  // Speak text in chunks (for long content)
  const speakLongText = useCallback(
    (text, speakOptions = {}) => {
      if (!text || text.trim() === '') {
        return;
      }

      // Split text into sentences for better handling of long content
      const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
      const chunks = [];
      let currentChunk = '';

      // Group sentences into chunks of ~200 characters
      for (const sentence of sentences) {
        if (currentChunk.length + sentence.length > 200) {
          if (currentChunk) {
            chunks.push(currentChunk.trim());
          }
          currentChunk = sentence;
        } else {
          currentChunk += sentence;
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk.trim());
      }

      // Speak chunks sequentially
      let chunkIndex = 0;

      const speakNextChunk = () => {
        if (chunkIndex < chunks.length) {
          const utterance = new SpeechSynthesisUtterance(chunks[chunkIndex]);
          utterance.voice = speakOptions.voice || currentVoice;
          utterance.rate = speakOptions.rate ?? rate;
          utterance.pitch = speakOptions.pitch ?? pitch;
          utterance.lang = speakOptions.language || language;

          utterance.onstart = () => {
            if (chunkIndex === 0) {
              setIsSpeaking(true);
              setIsPaused(false);
              if (onStart) onStart();
            }
          };

          utterance.onend = () => {
            chunkIndex++;
            if (chunkIndex < chunks.length) {
              speakNextChunk();
            } else {
              setIsSpeaking(false);
              setIsPaused(false);
              if (onEnd) onEnd();
            }
          };

          utterance.onerror = (event) => {
            if (event.error !== 'interrupted') {
              setError(`Speech error: ${event.error}`);
              setIsSpeaking(false);
            }
          };

          utteranceRef.current = utterance;
          window.speechSynthesis.speak(utterance);
        }
      };

      // Cancel any ongoing speech and start
      window.speechSynthesis.cancel();
      speakNextChunk();
    },
    [currentVoice, rate, pitch, language, onStart, onEnd]
  );

  // Cancel speech
  const cancel = useCallback(() => {
    if (browserSupported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
    }
  }, []);

  // Pause speech
  const pause = useCallback(() => {
    if (browserSupported && isSpeaking && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, [isSpeaking, isPaused]);

  // Resume speech
  const resume = useCallback(() => {
    if (browserSupported && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, [isPaused]);

  // Toggle pause/resume
  const togglePause = useCallback(() => {
    if (isPaused) {
      resume();
    } else if (isSpeaking) {
      pause();
    }
  }, [isPaused, isSpeaking, pause, resume]);

  // Set voice by name or voice object
  const setVoice = useCallback(
    (voiceOrName) => {
      if (typeof voiceOrName === 'string') {
        const voice = voices.find((v) => v.name === voiceOrName);
        if (voice) {
          setCurrentVoice(voice);
        }
      } else {
        setCurrentVoice(voiceOrName);
      }
    },
    [voices]
  );

  // Get voices filtered by language
  const getVoicesByLanguage = useCallback(
    (lang) => {
      return voices.filter((voice) => voice.lang.startsWith(lang.split('-')[0]));
    },
    [voices]
  );

  return {
    // State
    isSpeaking,
    isPaused,
    voices,
    currentVoice,
    rate,
    pitch,
    error,
    browserSupported,

    // Controls
    speak,
    speakLongText,
    cancel,
    pause,
    resume,
    togglePause,

    // Settings
    setVoice,
    setRate,
    setPitch,
    getVoicesByLanguage,
  };
};

export default useSpeechSynthesis;
export { browserSupported as speechSynthesisSupported };
